/**
 * ContractSwitcher — dropdown header per cambiare contratto/POD attivo.
 *
 * Mostra sia i **contratti propri** sia i contratti in **monitoraggio**
 * (POD di terzi che l'utente può consultare). Replica il pattern mobile
 * Route.js dove `[...contratti, ...monitoraggio]` viene trattato come una
 * lista unica.
 *
 * Allo switch:
 *   - aggiorna `activeContractCode` in Zustand (persistito)
 *   - tutte le query downstream (bollette, consumi, letture) si rifanno
 *     automaticamente perché la query key dipende da `codice_contratto`
 */

import { Building2, Check, ChevronDown, Eye } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/features/auth/authStore'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import type { Contratto } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

export function ContractSwitcher() {
  const { contratti, monitoraggio, activeContract } = useActiveContract()
  const setActiveContract = useAuthStore((s) => s.setActiveContract)

  // Nascondo lo switcher se il totale dei contratti (propri + monitorati)
  // è 0 o 1: in quei casi non c'è scelta da fare e mostrarlo aggiunge
  // solo rumore visivo (stesso criterio del mobile Route.js).
  if (contratti.length + monitoraggio.length < 2) return null

  // Identifico se l'active è un contratto proprio o monitorato (per icona).
  const isMonitored = monitoraggio.some(
    (c) => c.codice_contratto === activeContract?.codice_contratto,
  )
  const TriggerIcon = isMonitored ? Eye : Building2

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 max-w-[10rem] items-center gap-2 rounded-full border border-border bg-card px-3 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-[16rem]"
          aria-label="Cambia contratto attivo"
        >
          <TriggerIcon className="h-4 w-4 shrink-0 text-secondary" />
          <span className="flex-1 truncate font-medium text-foreground">
            {labelFor(activeContract)}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[18rem]">
        {contratti.length > 0 && (
          <>
            <DropdownMenuLabel>I tuoi contratti</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {contratti.map((c) => (
              <ContractItem
                key={c.codice_contratto}
                contract={c}
                isActive={c.codice_contratto === activeContract?.codice_contratto}
                onSelect={() => setActiveContract(c.codice_contratto)}
                icon={Building2}
              />
            ))}
          </>
        )}

        {monitoraggio.length > 0 && (
          <>
            {contratti.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>Monitoraggio</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {monitoraggio.map((c) => (
              <ContractItem
                key={c.codice_contratto}
                contract={c}
                isActive={c.codice_contratto === activeContract?.codice_contratto}
                onSelect={() => setActiveContract(c.codice_contratto)}
                icon={Eye}
              />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type ContractItemProps = {
  contract: Contratto
  isActive: boolean
  onSelect: () => void
  icon: typeof Building2
}

function ContractItem({ contract, isActive, onSelect, icon: Icon }: ContractItemProps) {
  return (
    <DropdownMenuItem onSelect={onSelect}>
      <div className="flex w-full items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
        <div className="flex flex-1 flex-col">
          <span className="text-sm font-semibold text-foreground">
            {labelFor(contract)}
          </span>
          {contract.pod.etichetta && contract.pod.indirizzo && (
            <span className="truncate text-xs text-muted-foreground">
              {contract.pod.indirizzo}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            POD {contract.pod.pod}
          </span>
        </div>
        <Check
          className={cn(
            'mt-0.5 h-4 w-4 text-secondary transition-opacity',
            isActive ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
    </DropdownMenuItem>
  )
}

function labelFor(c: Contratto | null): string {
  if (!c) return 'Contratto'
  return c.pod.etichetta || c.pod.indirizzo || c.pod.pod || 'Contratto'
}
